import { canvas, resizeCanvasToDisplaySize } from "./gl.js";
import { clamp } from "./utils.js";
import { DEFAULT_BOX_SIZE, CHARACTER_SCALE, editor } from "./state.js";

export function computePanelRect(canvasWidth, canvasHeight) {
  const targetAspect = 16 / 9;
  const canvasAspect = canvasWidth / canvasHeight;

  let panelWidth = canvasWidth;
  let panelHeight = canvasHeight;

  if (canvasAspect > targetAspect) {
    panelHeight = canvasHeight;
    panelWidth = panelHeight * targetAspect;
  } else {
    panelWidth = canvasWidth;
    panelHeight = panelWidth / targetAspect;
  }

  const left = (canvasWidth - panelWidth) * 0.5;
  const bottom = (canvasHeight - panelHeight) * 0.5;
  return { left, bottom, width: panelWidth, height: panelHeight };
}

export function computeContainedRect(bounds, contentSize, heightFactor) {
  if (!contentSize || contentSize.width === 0 || contentSize.height === 0) {
    return bounds;
  }
  const maxWidth = bounds.width;
  const maxHeight = bounds.height * heightFactor;
  const scale = Math.min(maxWidth / contentSize.width, maxHeight / contentSize.height);
  const width = contentSize.width * scale;
  const height = contentSize.height * scale;
  const left = bounds.left + (bounds.width - width) * 0.5;
  const bottom = bounds.bottom + (bounds.height - height) * 0.5;
  return { left, bottom, width, height };
}

export function clampBoxCenterNormalized(x, y, size, panelRect) {
  const minDim = Math.min(panelRect.width, panelRect.height);
  const halfSizePx = size * minDim * 0.5;
  const halfW = halfSizePx / panelRect.width;
  const halfH = halfSizePx / panelRect.height;
  return {
    x: clamp(x, halfW, 1 - halfW),
    y: clamp(y, halfH, 1 - halfH),
  };
}

export function clampBoxesToPanel(panelRect) {
  for (const box of editor.boxes) {
    const clamped = clampBoxCenterNormalized(box.x, box.y, box.size, panelRect);
    box.x = clamped.x;
    box.y = clamped.y;
  }
}

export function getPanelRectForInput() {
  resizeCanvasToDisplaySize();
  const panelRect = computePanelRect(canvas.width, canvas.height);
  clampBoxesToPanel(panelRect);
  return panelRect;
}

export function getCanvasPoint(event) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const x = (event.clientX - rect.left) * scaleX;
  const yFromTop = (event.clientY - rect.top) * scaleY;
  return { x, y: canvas.height - yFromTop };
}

export function pointInRect(point, rect) {
  return (
    point.x >= rect.left &&
    point.x <= rect.left + rect.width &&
    point.y >= rect.bottom &&
    point.y <= rect.bottom + rect.height
  );
}

export function getBoxById(id) {
  return editor.boxes.find((box) => box.id === id) || null;
}

export function getBoxRectPixels(box, panelRect) {
  const centerX = panelRect.left + box.x * panelRect.width;
  const centerY = panelRect.bottom + box.y * panelRect.height;
  const sizePx = getBoxPixelSize(box, panelRect);
  const width = sizePx;
  const height = sizePx;
  return {
    left: centerX - width * 0.5,
    bottom: centerY - height * 0.5,
    width,
    height,
  };
}

export function getBoxCenterPixels(box, panelRect) {
  return {
    x: panelRect.left + box.x * panelRect.width,
    y: panelRect.bottom + box.y * panelRect.height,
  };
}

export function getBoxPixelSize(box, panelRect) {
  const minDim = Math.min(panelRect.width, panelRect.height);
  return box.size * minDim;
}

export function normalizedToPixel(pos, panelRect) {
  return {
    x: panelRect.left + pos.x * panelRect.width,
    y: panelRect.bottom + pos.y * panelRect.height,
  };
}

export function pixelToNormalized(pos, panelRect) {
  return {
    x: (pos.x - panelRect.left) / panelRect.width,
    y: (pos.y - panelRect.bottom) / panelRect.height,
  };
}

export function getBenchBaseSize(panelRect) {
  const startBox = editor.startBoxId != null ? getBoxById(editor.startBoxId) : null;
  if (startBox) {
    return getBoxPixelSize(startBox, panelRect) * CHARACTER_SCALE;
  }
  const minDim = Math.min(panelRect.width, panelRect.height);
  return minDim * DEFAULT_BOX_SIZE * CHARACTER_SCALE;
}
